import axios, { type AxiosInstance, type AxiosRequestConfig } from 'axios';
import { isNode } from 'browser-or-node';
import _ from 'lodash';

import { ApiModelClient } from './ApiModelClient';
import { API_TRACKING_HEADER, MODEL_NAMES } from './constants';
import { getSetupOptions, setupOnce } from './setup';

import type { ApiTrackingHeader, ModelName, SetupOptions } from './typeDefs';

let instance: ApiClient | null = null;

export interface ApiClientOptions extends Partial<SetupOptions> {
    /** do not memoize it as a global client shared everywhere */
    local?: boolean;
    /**
     * Do not check for valid tokens before sending requests.
     * Only enable this if working with public routes (like /auth/login)
     */
    skipCredentialCheck?: boolean;
}

export class ApiClient {
    #axiosClient?: AxiosInstance;
    #defaultHeaders?: Record<string, string> = {};
    #tracking?: ApiTrackingHeader;

    constructor(private options: ApiClientOptions = {}) {
        if (!options.local) instance = instance || this;
    }

    get client(): AxiosInstance {
        setupOnce({}); // setup once to pick up env vars

        const setupOptions = { ...getSetupOptions(), ...this.options };
        const [url, token] = [setupOptions.apiV2Url, setupOptions.apiV2Token];

        const credentialsError = (type: string) =>
            new Error(
                [
                    `[api-client:v2] invalid ${type}. Either:`,
                    `- set the following ENV var: process.env.API_V2_${type.toUpperCase()}`,
                    `- call setup() and provide a valid configuration for: apiV2${_.upperFirst(
                        type,
                    )}`,
                ].join('\n'),
            );

        if (!url) throw credentialsError('url');
        if (!this.options.skipCredentialCheck && !token) throw credentialsError('token');
        if (this.#axiosClient) return this.#axiosClient;

        let httpsAgent: any = undefined;

        // In Node environment it's useful to ignore SSL issues
        if (isNode) {
            // requiring inline so it works in both browser and Node
            httpsAgent = new (require('https') as any).Agent({
                rejectUnauthorized: false,
            });
        }

        this.#axiosClient = axios.create({
            baseURL: url,
            httpsAgent,
            headers: {
                ...setupOptions.defaultHeaders,
                'Content-Type': 'application/json',
                // A string token is baked in here as it always has been. A getter is resolved per
                // request by the interceptor below, since the axios instance is cached for the
                // lifetime of the process and a rotating token would otherwise never propagate.
                ...(typeof token === 'function' ? {} : { JWT: token }),
            },
        });

        if (typeof token === 'function') {
            const getToken = token;
            this.#axiosClient.interceptors.request.use(async config => {
                config.headers = config.headers || {};
                config.headers.JWT = await getToken();
                return config;
            });
        }

        return this.#axiosClient;
    }

    setDefaultHeaders(headers: Record<string, string>): void {
        this.#defaultHeaders = headers;
    }

    setTracking(tracking: ApiTrackingHeader): void {
        this.#tracking = tracking;
    }

    serializeParams(params: any): void {
        if (!params || typeof params !== 'object') return undefined;

        params = { ...params };

        if (params.query != null && typeof params.query === 'object') {
            params.query = JSON.stringify(params.query);
        }

        return params;
    }

    getDefaultHeaders(): Record<string, string> {
        const headers = {
            ...this.#defaultHeaders,
        };

        const tracking = this.#tracking || getSetupOptions().tracking;
        if (tracking) headers[API_TRACKING_HEADER] = JSON.stringify(tracking);

        return headers;
    }

    async fetch(
        method: 'get' | 'post' | 'patch' | 'put' | 'delete',
        url: string,
        options: AxiosRequestConfig = {},
    ): Promise<any> {
        if (!/^(get|post|patch|delete|put)$/i.test(method)) {
            throw new Error(`Invalid method '${method}'`);
        }

        if (!url) throw new Error('Missing url');

        try {
            const { data } = await this.client.request({
                ...options,
                headers: {
                    ...this.getDefaultHeaders(),
                    ...options.headers,
                },
                params: this.serializeParams(options.params || {}),
                method,
                url,
            });

            return data;
        } catch (ex: any) {
            if (ex.response?.data) throw ex.response.data;
            if (ex.code) throw new Error(`${ex.code} for ${method} ${url}`);
            throw ex;
        }
    }

    get<R = any>(
        url: string,
        params?: AxiosRequestConfig['params'],
        options?: AxiosRequestConfig,
    ): Promise<R> {
        return this.fetch('get', url, { ...options, params });
    }

    post<R = any>(
        url: string,
        data: AxiosRequestConfig['data'],
        options?: AxiosRequestConfig,
    ): Promise<R> {
        return this.fetch('post', url, { ...options, data });
    }

    patch<R = any>(
        url: string,
        data: AxiosRequestConfig['data'],
        options?: AxiosRequestConfig,
    ): Promise<R> {
        return this.fetch('patch', url, { ...options, data });
    }

    delete<R = any>(url: string, options?: AxiosRequestConfig): Promise<R> {
        return this.fetch('delete', url, options);
    }

    /**
     * Returns an ApiModelProxy which is an API client that mimicks the mongoose model interface
     *
     * @example
     *  const Image = api.model<ImageT>('Image');
     *  await Image.find({ query: { ... }, select: 'name' });
     *  await Image.aggregate([ { $match: ... }, { $project: ... } ]);
     *  await Image.deleteMany({ query: ... });
     */
    model = _.memoize(
        <Name extends ModelName>(modelName: Name) => {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const instance: ApiClient = this;

            return new ApiModelClient(instance, modelName);
        },
        model => `${model}`,
    );

    /**
     * Returns a map of all available models.
     *
     * If you don't need to specify the datatype, this is a more convenient method than `.model()`
     *
     * @example
     *  const { Company, Image, Pv2 } = api.getModels();
     *  await Pv2.find(...);
     */
    getModels(): { [key in ModelName]: ApiModelClient<key> } {
        return Object.fromEntries(MODEL_NAMES.map(name => [name, this.model(name)])) as {
            [key in ModelName]: ApiModelClient<key>;
        };
    }
}
